import React, { useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import { formatNumber } from "../../utils/format";
import ErrorModal from "../../components/ErrorModal";
import { usePermission } from "../../hooks/usePermission";

const ENTRY_TYPE_LABELS = {
  huur: "Huur",
  divers_geen_btw: "Divers (0%)",
  divers_met_btw: "Divers (BTW)",
};

function buildWeeks(year) {
  const weeks = [];
  let current = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  let weekNr = 1;

  while (current <= yearEnd) {
    const dow = current.getDay(); // 0=Sun, 5=Fri, 6=Sat
    // Days to next Friday
    const daysToFriday = (5 - current.getDay() + 7) % 7;
    let weekEnd = new Date(current);
    weekEnd.setDate(current.getDate() + daysToFriday);
    if (weekEnd > yearEnd) weekEnd = new Date(yearEnd);

    const fmt = (d) =>
      `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;

    weeks.push({
      week_nr: weekNr,
      week_start: current.toISOString().split("T")[0],
      week_end: weekEnd.toISOString().split("T")[0],
      label: `W${String(weekNr).padStart(2, "0")}: ${fmt(current)} - ${fmt(weekEnd)}`,
    });

    current = new Date(weekEnd);
    current.setDate(weekEnd.getDate() + 1);
    weekNr++;
  }
  return weeks;
}

export default function ResultsManualEntries({ user }) {
  const { hasPermission } = usePermission();
  const canImport = hasPermission("results.import");

  const currentYear = new Date().getFullYear();
  const [jaar, setJaar] = useState(currentYear);
  const [members, setMembers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  // Weeks for selected year
  const weeks = buildWeeks(jaar);

  // Form state
  const [form, setForm] = useState({
    week_nr: "",
    member_id: "",
    entry_type: "huur",
    bedrag: "",
    notitie: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiFetch("/results/members")
      .then((data) => setMembers(data.filter((m) => m.type === "huurder" || m.is_active)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [jaar]);

  const fetchEntries = () => {
    setLoading(true);
    apiFetch(`/results/manual-entries?jaar=${jaar}`)
      .then(setEntries)
      .catch((err) =>
        setErrorModal({ show: true, message: err.message || "Fout bij ophalen van ingaves." })
      )
      .finally(() => setLoading(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.week_nr || !form.bedrag) return;

    const week = weeks.find((w) => w.week_nr === parseInt(form.week_nr, 10));
    if (!week) return;

    setSubmitting(true);
    try {
      await apiFetch("/results/manual-entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          week_start: week.week_start,
          week_end: week.week_end,
          jaar,
          week_nr: week.week_nr,
          member_id: form.member_id || null,
          entry_type: form.entry_type,
          bedrag: parseFloat(form.bedrag),
          notitie: form.notitie || null,
        }),
      });
      setForm({ week_nr: "", member_id: "", entry_type: "huur", bedrag: "", notitie: "" });
      fetchEntries();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij opslaan." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Ingave verwijderen?")) return;
    try {
      await apiFetch(`/results/manual-entries/${entryId}`, { method: "DELETE" });
      fetchEntries();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij verwijderen." });
    }
  };

  // Group entries by week_nr
  const byWeek = {};
  entries.forEach((e) => {
    if (!byWeek[e.week_nr]) byWeek[e.week_nr] = [];
    byWeek[e.week_nr].push(e);
  });
  const weekNrsWithEntries = Object.keys(byWeek)
    .map(Number)
    .sort((a, b) => a - b);

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onClose={() => setErrorModal({ show: false, message: "" })}
        />
      )}

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Weekingave</h1>
        <select
          value={jaar}
          onChange={(e) => setJaar(Number(e.target.value))}
          className="ml-auto border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Add form */}
      {canImport && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-6">
          <h2 className="text-base font-semibold text-gray-700 mb-4">Nieuwe ingave</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Week</label>
              <select
                value={form.week_nr}
                onChange={(e) => setForm({ ...form, week_nr: e.target.value })}
                required
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecteer week</option>
                {weeks.map((w) => (
                  <option key={w.week_nr} value={w.week_nr}>{w.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Medewerker</label>
              <select
                value={form.member_id}
                onChange={(e) => setForm({ ...form, member_id: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Divers —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.naam}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={form.entry_type}
                onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="huur">Huur</option>
                <option value="divers_geen_btw">Divers (0%)</option>
                <option value="divers_met_btw">Divers (BTW)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bedrag (€)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.bedrag}
                onChange={(e) => setForm({ ...form, bedrag: e.target.value })}
                required
                placeholder="0,00"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notitie</label>
              <input
                type="text"
                value={form.notitie}
                onChange={(e) => setForm({ ...form, notitie: e.target.value })}
                placeholder="Optioneel..."
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? "Opslaan..." : "Toevoegen"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Entries grouped by week */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : weekNrsWithEntries.length === 0 ? (
        <div className="text-gray-400 text-sm text-center py-12">
          Geen ingaves voor {jaar}.
        </div>
      ) : (
        <div className="space-y-4">
          {weekNrsWithEntries.map((wn) => {
            const weekMeta = weeks.find((w) => w.week_nr === wn);
            const weekEntries = byWeek[wn];
            const weekTotal = weekEntries.reduce((s, e) => s + parseFloat(e.bedrag), 0);

            return (
              <div key={wn} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">
                    {weekMeta ? weekMeta.label : `W${String(wn).padStart(2, "0")}`}
                  </span>
                  <span className="text-sm font-semibold text-gray-600">
                    € {formatNumber(weekTotal, 2)}
                  </span>
                </div>
                <table className="min-w-full text-sm">
                  <tbody>
                    {weekEntries.map((entry) => (
                      <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-700">
                          {entry.member_naam || "Divers"}
                        </td>
                        <td className="px-4 py-2 text-gray-500 text-xs">
                          {ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-800">
                          € {formatNumber(entry.bedrag, 2)}
                        </td>
                        <td className="px-4 py-2 text-gray-400 text-xs">
                          {entry.notitie || ""}
                        </td>
                        {canImport && (
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Verwijderen
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
